import { LinkTo } from '@ember/routing';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { FEATURE_TOGGLES } from 'lumen/utils/constants/feature-toggle-constants';
import {
  getFeaturesForRoute,
  getPreferenceGroup,
} from 'lumen/utils/constants/navigation';

interface HotelAndMarketNavigationNavigationLinkSignature {
  Args: {
    currentWhen?: string | boolean;
    disabled?: boolean;
    externalUrl?: string;
    featureName?: string | string[];
    queryParams?: Record<string, unknown>;
    routeName: string;
  };
  Blocks: {
    default: [];
  };
  Element: HTMLAnchorElement;
}

/**
 * A navigation LinkTo component that automatically determines the correct subscription ID
 * for unified hotel view navigation.
 *
 * When the UNIFIED_HOTEL_VIEW feature toggle is enabled, delegates to
 * navigation.getSubscriptionId which prioritises persisted preferences, then current
 * subscription, then first matching subscription. The feature list comes from
 * @featureName if provided, otherwise falls back to ROUTE_FEATURE_MAP.
 */
export default class HotelAndMarketNavigationNavigationLink extends Component<HotelAndMarketNavigationNavigationLinkSignature> {
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare navigation: Services['navigation'];

  get modelId(): string | undefined {
    const { featureName, routeName } = this.args;

    // Only handle hotel routes
    if (!routeName.startsWith('hotel.')) {
      return undefined;
    }

    const hasUnifiedToggle = this.featureToggle.hasFeatureToggle(
      FEATURE_TOGGLES.UNIFIED_HOTEL_VIEW,
    );

    const resolved = this.resolvedRouteName;
    // When the route was overridden by a preference group, use the resolved
    // route's features — not the original @featureName which may not match.
    const features =
      resolved !== routeName
        ? getFeaturesForRoute(resolved)
        : (featureName ?? getFeaturesForRoute(resolved));

    if (features && hasUnifiedToggle) {
      return this.navigation.getSubscriptionId(features, resolved);
    }

    // Fallback to current hotel ID
    const hotelId = this.hotelAndMarketService.hotel?.id;

    return hotelId ? hotelId : undefined;
  }

  get query(): Record<string, unknown> {
    return this.args.queryParams ?? {};
  }

  get resolvedRouteName(): string {
    const { routeName } = this.args;

    if (!getPreferenceGroup(routeName)) {
      return routeName;
    }

    return this.navigation.getPersistedRoute(routeName) ?? routeName;
  }

  <template>
    {{#if @externalUrl}}
      <a
        href={{@externalUrl}}
        rel="noopener noreferrer"
        target="_blank"
        ...attributes
      >
        {{yield}}
      </a>
    {{else if this.modelId}}
      <LinkTo
        @current-when={{@currentWhen}}
        @disabled={{@disabled}}
        @model={{this.modelId}}
        @query={{this.query}}
        @route={{this.resolvedRouteName}}
        ...attributes
      >
        {{yield}}
      </LinkTo>
    {{else}}
      <LinkTo
        @current-when={{@currentWhen}}
        @disabled={{@disabled}}
        @query={{this.query}}
        @route={{this.resolvedRouteName}}
        ...attributes
      >
        {{yield}}
      </LinkTo>
    {{/if}}
  </template>
}
